// Module Vuex pour la gestion des propriétés avec Dexie
import dexieService from '@/services/dexieService'

const state = {
  properties: [],
  filteredProperties: [],
  loading: false,
  error: null,
  filters: {
    team: null,
    status: null,
    hasPlanification: null,
    hasNotPlanification: null,
    propertyType: null,
    manager: null,
    assistance: null,
    owner: null,
    groupOwner: null,
    unit: null,
    subUnit: null,
    nsil: '',
    city: '',
    search: ''
  },
  pagination: {
    page: 1,
    limit: 12,
    total: 0,
    hasMore: false
  },
  stats: {
    total: 0,
    vacant: 0,
    occupied: 0,
    planned: 0,
    unplanned: 0
  }
}

const mutations = {
  SET_PROPERTIES(state, properties) {
    state.properties = properties
  },
  SET_FILTERED_PROPERTIES(state, properties) {
    state.filteredProperties = properties
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters }
  },
  RESET_FILTERS(state) {
    state.filters = {
      team: null,
      status: null,
      hasPlanification: null,
      hasNotPlanification: null,
      propertyType: null,
      manager: null,
      assistance: null,
      owner: null,
      groupOwner: null,
      unit: null,
      subUnit: null,
      nsil: '',
      city: '',
      search: ''
    }
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination }
  },
  SET_STATS(state, stats) {
    state.stats = { ...state.stats, ...stats }
  },
  ADD_PROPERTY(state, property) {
    state.properties.push(property)
  },
  UPDATE_PROPERTY(state, { id, updates }) {
    const index = state.properties.findIndex(p => p.id === id)
    if (index !== -1) {
      state.properties[index] = { ...state.properties[index], ...updates }
    }
  },
  REMOVE_PROPERTY(state, id) {
    state.properties = state.properties.filter(p => p.id !== id)
  }
}

const actions = {
  // Charger toutes les propriétés depuis Dexie
  async loadAllProperties({ commit }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const properties = await dexieService.getAllProperties()
      commit('SET_PROPERTIES', properties)
      
      // Calculer les statistiques
      const stats = calculateStats(properties)
      commit('SET_STATS', stats)
      
      return properties
    } catch (error) {
      console.error('Erreur lors du chargement des propriétés:', error)
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Filtrer les propriétés selon les critères
  async filterProperties({ commit, state }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const filteredProperties = await dexieService.filterProperties(state.filters)
      commit('SET_FILTERED_PROPERTIES', filteredProperties)
      
      // Mettre à jour la pagination
      commit('SET_PAGINATION', {
        total: filteredProperties.length,
        page: 1 // Reset à la première page lors d'un nouveau filtrage
      })
      
      return filteredProperties
    } catch (error) {
      console.error('Erreur lors du filtrage des propriétés:', error)
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Obtenir les propriétés paginées
  async getPaginatedProperties({ commit, state }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const result = await dexieService.getPaginatedProperties(
        state.filters,
        state.pagination.page,
        state.pagination.limit
      )
      
      commit('SET_FILTERED_PROPERTIES', result.data)
      commit('SET_PAGINATION', {
        page: result.page,
        limit: result.limit,
        total: result.total,
        hasMore: result.hasMore
      })
      
      return result
    } catch (error) {
      console.error('Erreur lors de la pagination des propriétés:', error)
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Ajouter une propriété
  async addProperty({ commit }, property) {
    try {
      const success = await dexieService.addProperty(property)
      if (success) {
        commit('ADD_PROPERTY', property)
      }
      return success
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la propriété:', error)
      commit('SET_ERROR', error.message)
      throw error
    }
  },

  // Mettre à jour une propriété
  async updateProperty({ commit }, { id, updates }) {
    try {
      const property = await dexieService.getProperty(id)
      if (property) {
        const updatedProperty = { ...property, ...updates }
        const success = await dexieService.addProperty(updatedProperty) // put = update or insert
        if (success) {
          commit('UPDATE_PROPERTY', { id, updates })
        }
        return success
      }
      return false
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la propriété:', error)
      commit('SET_ERROR', error.message)
      throw error
    }
  },

  // Supprimer une propriété
  async removeProperty({ commit }, id) {
    try {
      const success = await dexieService.removeProperty(id)
      if (success) {
        commit('REMOVE_PROPERTY', id)
      }
      return success
    } catch (error) {
      console.error('Erreur lors de la suppression de la propriété:', error)
      commit('SET_ERROR', error.message)
      throw error
    }
  },

  // Obtenir une propriété par ID
  async getProperty({ state }, id) {
    try {
      // D'abord chercher dans le state local
      let property = state.properties.find(p => p.id === id)
      
      // Si pas trouvée, chercher dans Dexie
      if (!property) {
        property = await dexieService.getProperty(id)
      }
      
      return property
    } catch (error) {
      console.error('Erreur lors de la récupération de la propriété:', error)
      throw error
    }
  },

  // Compter les propriétés
  async countProperties() {
    try {
      return await dexieService.countProperties()
    } catch (error) {
      console.error('Erreur lors du comptage des propriétés:', error)
      throw error
    }
  },

  // Vérifier si des données locales existent
  async hasLocalData() {
    try {
      return await dexieService.hasLocalData()
    } catch (error) {
      console.error('Erreur lors de la vérification des données locales:', error)
      return false
    }
  },

  // Actions pour les filtres
  setFilters({ commit, dispatch }, filters) {
    commit('SET_FILTERS', filters)
    // Relancer le filtrage automatiquement
    return dispatch('getPaginatedProperties')
  },

  resetFilters({ commit, dispatch }) {
    commit('RESET_FILTERS')
    commit('SET_PAGINATION', { page: 1 })
    // Relancer le filtrage automatiquement
    return dispatch('getPaginatedProperties')
  },

  // Actions pour la pagination
  setPage({ commit, dispatch }, page) {
    commit('SET_PAGINATION', { page })
    return dispatch('getPaginatedProperties')
  },

  setLimit({ commit, dispatch }, limit) {
    commit('SET_PAGINATION', { limit, page: 1 }) // Reset à la première page
    return dispatch('getPaginatedProperties')
  },

  // Aller à la page suivante
  async nextPage({ commit, dispatch, state }) {
    if (state.pagination.hasMore) {
      const nextPage = state.pagination.page + 1
      commit('SET_PAGINATION', { page: nextPage })
      return dispatch('getPaginatedProperties')
    }
  },

  // Aller à la page précédente
  async previousPage({ commit, dispatch, state }) {
    if (state.pagination.page > 1) {
      const prevPage = state.pagination.page - 1
      commit('SET_PAGINATION', { page: prevPage })
      return dispatch('getPaginatedProperties')
    }
  },

  // Obtenir les statistiques des données locales
  async getLocalDataStats() {
    try {
      return await dexieService.getLocalDataStats()
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error)
      throw error
    }
  }
}

const getters = {
  // Propriétés de base
  properties: state => state.properties,
  filteredProperties: state => state.filteredProperties,
  loading: state => state.loading,
  error: state => state.error,
  
  // Filtres et pagination
  filters: state => state.filters,
  pagination: state => state.pagination,
  
  // Statistiques
  stats: state => state.stats,
  
  // Propriétés calculées
  totalProperties: state => state.properties.length,
  filteredCount: state => state.filteredProperties.length,
  
  // Vérifications
  hasProperties: state => state.properties.length > 0,
  hasFilteredProperties: state => state.filteredProperties.length > 0,
  hasFilters: state => {
    return Object.values(state.filters).some(value => 
      value !== null && value !== '' && value !== undefined
    )
  },
  
  // Pagination calculée
  currentPage: state => state.pagination.page,
  totalPages: state => Math.ceil(state.pagination.total / state.pagination.limit),
  hasNextPage: state => state.pagination.hasMore,
  hasPreviousPage: state => state.pagination.page > 1,
  
  // Propriétés par statut
  vacantProperties: state => state.properties.filter(p => p.occupancy === 'Vacant'),
  occupiedProperties: state => state.properties.filter(p => p.occupancy === 'Occupé'),
  plannedProperties: state => state.properties.filter(p => p.planifiedAt),
  unplannedProperties: state => state.properties.filter(p => !p.planifiedAt)
}

// Fonction utilitaire pour calculer les statistiques
function calculateStats(properties) {
  const stats = {
    total: properties.length,
    vacant: 0,
    occupied: 0,
    planned: 0,
    unplanned: 0
  }
  
  properties.forEach(property => {
    // Compter par occupancy
    if (property.occupancy === 'Vacant') {
      stats.vacant++
    } else if (property.occupancy === 'Occupé') {
      stats.occupied++
    }
    
    // Compter par planification
    if (property.planifiedAt) {
      stats.planned++
    } else {
      stats.unplanned++
    }
  })
  
  return stats
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
